import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { OrgChartTreeHandler } from "./store/orgChartTreeSlice";
import { useNavigate } from "react-router-dom";
import { updateNavbarHeading } from "../../../constants/layout/store/filterSlice";
// Removed: import { Tab, Tabs } from "react-bootstrap";

const OrganizationChart = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Removed: const [activeTab, setActiveTab] = useState("immediateSuperior");
  const [immediateSuperiorTree, setImmediateSuperiorTree] = useState([]);
  const [practiceManagerTree, setPracticeManagerTree] = useState([]);
  const [immediateSuperiorCollapsed, setImmediateSuperiorCollapsed] = useState({});
  const [practiceManagerCollapsed, setPracticeManagerCollapsed] = useState({});
  const [immediateSuperiorSearch, setImmediateSuperiorSearch] = useState("");
  const [practiceManagerSearch, setPracticeManagerSearch] = useState("");
  const [filteredImmediateSuperior, setFilteredImmediateSuperior] = useState([]);
  const [filteredPracticeManager, setFilteredPracticeManager] = useState([]);

  // --- Responsive State Hook ---
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 992); // Breakpoint for responsive layout

  const handleResize = useCallback(() => {
    setIsMobileView(window.innerWidth < 992);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);
  // -----------------------------

  useEffect(() => {
    dispatch(OrgChartTreeHandler());
  }, [dispatch]);

  useEffect(() => {
    dispatch(updateNavbarHeading("Organization Chart"));
  }, []);

  const flatDataObjectImmediateSuperior = useSelector(
    ({ organizationChart }) => organizationChart?.orgChartTreeSlice?.data?.data
  );
  const flatDataObjectPracticeManager = useSelector(
    ({ organizationChart }) => organizationChart?.orgChartTreeSlice?.data?.orgpmdetails
  );

  // Build tree utility function
  const buildTreeData = (data) => {
    const buildTree = (nodes, pid = null) => {
      return nodes
        .filter((item) => {
          if (pid === null || pid === 0 || pid === "") {
            return item.PID === null || item.PID === 0 || item.PID === "";
          }
          return item.PID === pid;
        })
        .map((item) => ({
          ...item,
          children: buildTree(nodes, item.ID),
        }));
    };
    return buildTree(data, null);
  };
  
  // Set initial collapsed state utility function (collapsed if not the root)
  const setInitialCollapsedState = (nodes) => {
    const initialCollapsed = {};
    const setCollapsedState = (nodes) => {
      nodes.forEach((node) => {
        initialCollapsed[node.ID] = (node.PID !== null && node.PID !== 0 && node.PID !== "");
        if (node.children && node.children.length > 0) {
          setCollapsedState(node.children);
        }
      });
    };
    setCollapsedState(nodes);
    return initialCollapsed;
  };

  // Build immediate superior tree
  useEffect(() => {
    if (flatDataObjectImmediateSuperior) {
      const newTreeData = buildTreeData(flatDataObjectImmediateSuperior);
      setImmediateSuperiorTree(newTreeData);
      setFilteredImmediateSuperior(newTreeData);
      setImmediateSuperiorCollapsed(setInitialCollapsedState(newTreeData));
    }
  }, [flatDataObjectImmediateSuperior]);

  // Build practice manager tree
  useEffect(() => {
    if (flatDataObjectPracticeManager) {
      const newTreeData = buildTreeData(flatDataObjectPracticeManager);
      setPracticeManagerTree(newTreeData);
      setFilteredPracticeManager(newTreeData);
      setPracticeManagerCollapsed(setInitialCollapsedState(newTreeData));
    }
  }, [flatDataObjectPracticeManager]);

  // Function to expand all ancestors of a node
  const expandAncestors = (nodeId, treeData, setCollapsedFunction) => {
    const findNodeAndParents = (nodes, targetId, parents = []) => {
      for (const node of nodes) {
        if (node.ID === targetId) {
          return [...parents, node.ID];
        }
        if (node.children && node.children.length > 0) {
          const found = findNodeAndParents(node.children, targetId, [
            ...parents,
            node.ID,
          ]);
          if (found) return found;
        }
      }
      return null;
    };

    const nodePath = findNodeAndParents(treeData, nodeId);
    if (nodePath) {
      setCollapsedFunction((prev) => {
        const newState = { ...prev };
        // We only want to set parents to be expanded (false), not the node itself
        // nodePath contains the target node itself, so we exclude it.
        const parentIds = nodePath.slice(0, -1); 
        parentIds.forEach((id) => {
          newState[id] = false;
        });
        return newState;
      });
    }
  };

  // Filter tree based on search term (immediate superior)
  useEffect(() => {
    if (immediateSuperiorSearch.trim() === "") {
      setFilteredImmediateSuperior(immediateSuperiorTree);
      setImmediateSuperiorCollapsed(setInitialCollapsedState(immediateSuperiorTree));
      return;
    }

    const filterTree = (nodes) => {
      return nodes
        .map((node) => ({ ...node }))
        .filter((node) => {
          const matchesSearch = node.Name.toLowerCase().includes(
            immediateSuperiorSearch.toLowerCase()
          );

          if (node.children && node.children.length > 0) {
            const filteredChildren = filterTree(node.children);
            if (filteredChildren.length > 0) {
              node.children = filteredChildren;
              setImmediateSuperiorCollapsed((prev) => ({ ...prev, [node.ID]: false }));
              return true;
            }
          }

          if (matchesSearch) {
            expandAncestors(node.ID, immediateSuperiorTree, setImmediateSuperiorCollapsed);
            return true;
          }

          return false;
        });
    };

    setFilteredImmediateSuperior(filterTree(immediateSuperiorTree));
  }, [immediateSuperiorSearch, immediateSuperiorTree]);

  // Filter tree based on search term (practice manager)
  useEffect(() => {
    if (practiceManagerSearch.trim() === "") {
      setFilteredPracticeManager(practiceManagerTree);
      setPracticeManagerCollapsed(setInitialCollapsedState(practiceManagerTree));
      return;
    }

    const filterTree = (nodes) => {
      return nodes
        .map((node) => ({ ...node }))
        .filter((node) => {
          const matchesSearch = node.Name.toLowerCase().includes(
            practiceManagerSearch.toLowerCase()
          );

          if (node.children && node.children.length > 0) {
            const filteredChildren = filterTree(node.children);
            if (filteredChildren.length > 0) {
              node.children = filteredChildren;
              setPracticeManagerCollapsed((prev) => ({ ...prev, [node.ID]: false }));
              return true;
            }
          }

          if (matchesSearch) {
            expandAncestors(node.ID, practiceManagerTree, setPracticeManagerCollapsed);
            return true;
          }

          return false;
        });
    };

    setFilteredPracticeManager(filterTree(practiceManagerTree));
  }, [practiceManagerSearch, practiceManagerTree]);

  const toggleImmediateSuperiorCollapse = useCallback((id) => {
    setImmediateSuperiorCollapsed((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const togglePracticeManagerCollapse = useCallback((id) => {
    setPracticeManagerCollapsed((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const handleEmpProfile = (node) => {
    navigate(`/employee-profile/${node.ID}`, {
      state: { employeeData: node },
    });
  };

  const renderTree = (nodes, collapsedNodes, toggleCollapse, searchTerm) => {
    if (!nodes || nodes.length === 0) return null;

    return (
      <ul
        style={{
          position: "relative",
          marginLeft: "20px",
          paddingLeft: "10px",
          listStyle: "none",
        }}
      >
        {nodes.map((node) => (
          <li
            key={node.ID}
            style={{
              position: "relative",
              paddingBottom: "16px",
            }}
          >
            {/* Connector Lines - Only show if the node has a parent */}
            {node.PID !== null && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {/* Horizontal line */}
                <div
                  className="d-flex"
                  style={{
                    width: "16px",
                    height: "1px",
                    backgroundColor: "#326699",
                    marginRight: "2px",
                  }}
                ></div>

                {/* Small circle */}
                <div
                  style={{
                    width: "5px",
                    height: "5px",
                    backgroundColor:
                      node.children && node.children.length > 0
                        ? "#326699"
                        : "#ffffff",
                    borderRadius: "50%",
                    border: "1px solid #326699",
                  }}
                ></div>

                {/* Vertical line */}
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: "1px",
                    height: "100%",
                    backgroundColor: "#326699",
                  }}
                ></div>
              </div>
            )}

            {/* Node Card */}
            <div
              className="card org-chart-card"
              onClick={() => toggleCollapse(node.ID)}
              style={{
                maxWidth: "350px",
                padding: "10px 14px",
                marginLeft: "25px",
                border: "1px solid #e1e5e9",
                borderRadius: "10px",
                backgroundColor: "#ffffff",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "10px",
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.06)",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                position: "relative",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 3px 9px rgba(0, 0, 0, 0.1)";
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.borderColor = "#326699";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.06)";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.borderColor = "#e1e5e9";
              }}
            >
              {/* Profile Image with better styling */}
              <div style={{ position: "relative" }}>
                {node.ProfileImage ? (
                  <img
                    src={node.ProfileImage}
                    alt={node.Name}
                    style={{
                      width: "38px",
                      height: "38px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "2px solid #f0f4f8",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "38px",
                      height: "38px",
                      borderRadius: "50%",
                      backgroundColor: "#326699",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}
                  >
                    {node.Name ? node.Name.charAt(0).toUpperCase() : "U"}
                  </div>
                )}
                {/* Online indicator */}
                {/* <div
                  style={{
                    position: "absolute",
                    bottom: "1px",
                    right: "1px",
                    width: "10px",
                    height: "10px",
                    // backgroundColor: "#10b981",
                    border: "2px solid white",
                    borderRadius: "50%",
                  }}
                ></div> */}
              </div>

              {/* Info */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  textAlign: "left",
                  overflow: "hidden",
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    fontWeight: "700",
                    fontSize: "13px",
                    color: node.Name.toLowerCase().includes(searchTerm.toLowerCase())
                      ? "#2563eb"
                      : "#1e293b",
                    cursor: "pointer",
                    direction: /[\u0600-\u06FF]/.test(node.Name) ? "rtl" : "ltr",
                    lineHeight: "1.2",
                    marginBottom: "1px",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEmpProfile(node);
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = "#2563eb";
                    e.target.style.textDecoration = "underline";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = node.Name.toLowerCase().includes(searchTerm.toLowerCase())
                      ? "#2563eb"
                      : "#1e293b";
                    e.target.style.textDecoration = "none";
                  }}
                >
                  {node.Name}
                </span>

                {node.Designation && (
                  <span
                    style={{
                      color: "#64748b",
                      fontSize: "10px",
                      fontWeight: "500",
                      cursor: "pointer",
                      direction: /[\u0600-\u06FF]/.test(node.Designation) ? "rtl" : "ltr",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "block",
                      whiteSpace: "nowrap",
                      lineHeight: "1.1",
                    }}
                  >
                    {node.Designation} ({node.MemberSince})
                  </span>
                )}
                
                {/* {node.MemberSince && (
                  <span
                    style={{
                      color: "#94a3b8",
                      fontSize: "9px",
                      fontWeight: "400",
                      marginTop: "1px",
                    }}
                  >
                    Since {node.MemberSince}
                  </span>
                )} */}
              </div>

              {/* Expand/Collapse Icon */}
              {node.children && node.children.length > 0 && (
                <div
                  style={{
                    color: "#64748b",
                    fontSize: "10px",
                    fontWeight: "bold",
                    padding: "3px",
                    borderRadius: "3px",
                    backgroundColor: "#f8fafc",
                    minWidth: "18px",
                    textAlign: "center",
                  }}
                >
                  {collapsedNodes[node.ID] ? "+" : "−"}
                </div>
              )}
            </div>

            {/* Recursive Children */}
            {node.children &&
              node.children.length > 0 &&
              !collapsedNodes[node.ID] && (
                <div
                  style={{
                    marginLeft: "20px",
                    marginTop: "8px",
                    position: "relative",
                  }}
                >
                  {renderTree(node.children, collapsedNodes, toggleCollapse, searchTerm)}
                </div>
              )}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div
      style={{
        width: "100%",
        // Use a fixed height for demonstration, but typically this would be handled by the parent layout
        // For responsiveness, using `min-height: '100vh'` or similar might be better, 
        // but keeping original `height: '100vh'` for now.
        minHeight: "100", 
        margin: "0",
        padding: "10px",
        backgroundColor: "#f8fafc",
        overflow: "auto", // Changed from hidden to auto to allow scrolling if content overflows in mobile view
        boxSizing: 'border-box',
        display: 'flex', 
        flexDirection: 'column', 
      }}
    >
      {/* Main Content Container with Responsive Flex Direction */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "16px",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          border: "1px solid #e2e8f0",
          flex: 1, // Allow the container to grow and fill available space
          display: 'flex',
          // Key change: Use 'row' for desktop, 'column' for mobile
          flexDirection: isMobileView ? 'column' : 'row', 
          gap: isMobileView ? '30px' : '24px', // Increased gap for stacked view
          overflow: 'auto', // Allow scrolling within this container
        }}
      >
        {/* Immediate Supervisor Section */}
        <div 
          style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            minWidth: 0,
            // Add a margin bottom for mobile view separation if it's not the last item
            marginBottom: isMobileView ? '10px' : '0', 
            // In mobile view, ensure it spans full width
            width: isMobileView ? '100%' : 'auto', 
          }}
        >
          <h4 style={{ 
            fontSize: '15px', 
            fontWeight: '600', 
            color: '#1e293b', 
            marginBottom: '8px', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px' 
          }}>
            <i className="bi bi-diagram-3" style={{ fontSize: "15px" }}></i>
            Immediate Supervisor
          </h4>
          <div style={{
            position: 'relative',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
          }}>
            <i
              className="bi bi-search"
              style={{
                position: 'absolute',
                left: '12px',
                color: '#64748b',
                fontSize: '14px',
                pointerEvents: 'none',
                zIndex: 2,
              }}
            ></i>
            <input
              type="text"
              placeholder="Search employees by name..."
              value={immediateSuperiorSearch}
              onChange={(e) => setImmediateSuperiorSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 14px 10px 38px',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                fontSize: '13px',
                outline: 'none',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                transition: 'all 0.2s ease-in-out',
                backgroundColor: '#f8fafc',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#326699';
                e.target.style.backgroundColor = 'white';
                e.target.style.boxShadow = '0 3px 9px rgba(50, 102, 153, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.backgroundColor = '#f8fafc';
                e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
              }}
            />
          </div>
          
          {/* Tree Container */}
          <div
            style={{
              backgroundColor: '#f8fafc',
              borderRadius: '10px',
              padding: '16px',
              border: '1px solid #e2e8f0',
              flex: 1, 
              overflowY: 'auto',
              // Add a specific height for mobile view so the second chart is visible without scrolling up too much
              minHeight: isMobileView ? '400px' : 'auto',
            }}
          >
            {filteredImmediateSuperior.length > 0 ? (
              renderTree(filteredImmediateSuperior, immediateSuperiorCollapsed, toggleImmediateSuperiorCollapse, immediateSuperiorSearch)
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '40px 16px',
                  color: '#64748b',
                  minHeight: '300px',
                }}
              >
                <i
                  className="bi bi-people"
                  style={{
                    fontSize: '36px',
                    marginBottom: '12px',
                    opacity: 0.5,
                  }}
                ></i>
                <p
                  style={{
                    fontSize: '14px',
                    textAlign: 'center',
                    margin: '0',
                  }}
                >
                  {immediateSuperiorSearch.trim()
                    ? "No matching employees found"
                    : "Loading organizational chart..."}
                </p>
                {immediateSuperiorSearch.trim() && (
                  <p
                    style={{
                      fontSize: '12px',
                      color: '#94a3b8',
                      textAlign: 'center',
                      margin: '6px 0 0 0',
                    }}
                  >
                    Try adjusting your search terms
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Practice Manager Section */}
        <div 
          style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            minWidth: 0,
            width: isMobileView ? '100%' : 'auto',
          }}
        >
          <h4 style={{ 
            fontSize: '15px', 
            fontWeight: '600', 
            color: '#1e293b', 
            marginBottom: '8px', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px' 
          }}>
            <i className="bi bi-briefcase" style={{ fontSize: "15px" }}></i>
            Practice Manager
          </h4>
          <div style={{
            position: 'relative',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
          }}>
            <i
              className="bi bi-search"
              style={{
                position: 'absolute',
                left: '12px',
                color: '#64748b',
                fontSize: '14px',
                pointerEvents: 'none',
                zIndex: 2,
              }}
            ></i>
            <input
              type="text"
              placeholder="Search employees by name..."
              value={practiceManagerSearch}
              onChange={(e) => setPracticeManagerSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 14px 10px 38px',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                fontSize: '13px',
                outline: 'none',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                transition: 'all 0.2s ease-in-out',
                backgroundColor: '#f8fafc',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#326699';
                e.target.style.backgroundColor = 'white';
                e.target.style.boxShadow = '0 3px 9px rgba(50, 102, 153, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.backgroundColor = '#f8fafc';
                e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
              }}
            />
          </div>
          
          {/* Tree Container */}
          <div
            style={{
              backgroundColor: '#f8fafc',
              borderRadius: '10px',
              padding: '16px',
              border: '1px solid #e2e8f0',
              flex: 1, 
              overflowY: 'auto',
              minHeight: isMobileView ? '400px' : 'auto',
            }}
          >
            {filteredPracticeManager.length > 0 ? (
              renderTree(filteredPracticeManager, practiceManagerCollapsed, togglePracticeManagerCollapse, practiceManagerSearch)
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '40px 16px',
                  color: '#64748b',
                  minHeight: '300px',
                }}
              >
                <i
                  className="bi bi-briefcase"
                  style={{
                    fontSize: '36px',
                    marginBottom: '12px',
                    opacity: 0.5,
                  }}
                ></i>
                <p
                  style={{
                    fontSize: '14px',
                    textAlign: 'center',
                    margin: '0',
                  }}
                >
                  {practiceManagerSearch.trim()
                    ? "No matching employees found"
                    : "Loading organizational chart..."}
                </p>
                {practiceManagerSearch.trim() && (
                  <p
                    style={{
                      fontSize: '12px',
                      color: '#94a3b8',
                      textAlign: 'center',
                      margin: '6px 0 0 0',
                    }}
                  >
                    Try adjusting your search terms
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationChart;